/**
 * CollapsibleTable — a peek-and-expand wrapper for any table body. Collapsed, it
 * shows the first few rows clipped with a soft fade so the table stays visible;
 * a clear "Show all (…)" drop-down reveals the rest. Used by ComparisonTable and
 * PricingFeatureMatrix so long tables are previewable without forcing a scroll.
 * Tokens only.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils';

export interface CollapsibleTableProps {
  readonly title?: ReactNode;
  /** Short descriptor in the expand control, e.g. "26 rows" / "14 capabilities". */
  readonly summary?: ReactNode;
  /** Start fully expanded (default false → peek the first rows). */
  readonly defaultOpen?: boolean;
  /** Collapsed preview height in px — how much of the table peeks through. */
  readonly peekHeight?: number;
  readonly children: ReactNode;
  readonly className?: string;
}

export function CollapsibleTable({
  title, summary, defaultOpen = false, peekHeight = 240, children, className,
}: CollapsibleTableProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={cn('overflow-hidden rounded-card border border-border', className)}>
      {title && (
        <div className="border-b border-border bg-muted/40 px-4 py-2.5 font-display text-sm font-semibold text-foreground">
          {title}
        </div>
      )}
      <div className="relative">
        <div
          className={open ? 'overflow-x-auto' : 'overflow-hidden'}
          style={open ? undefined : { maxHeight: peekHeight }}
        >
          {children}
        </div>
        {!open && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-center gap-1.5 border-t border-border bg-muted/40 px-4 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-muted/60"
      >
        {open ? 'Show less' : <>Show all{summary ? <span className="text-muted-foreground"> ({summary})</span> : null}</>}
        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>
    </div>
  );
}
